import type { Metadata, Viewport } from "next";
import { env } from "@/lib/env";
import { CANONICAL_ORIGIN, SITE_DESCRIPTION, SITE_NAME } from "./config";
import { getOpenGraphLocale } from "./seo/locale-meta";
import {
  getCanonicalUrl,
  getLangAlternates,
  getSocialMetadata,
  pageKeywords,
} from "./seo/meta-helpers";

// Base metadata for use with Next.js 15.3.1 static metadata exports
export const baseMetadata: Metadata = {
  metadataBase: new URL(CANONICAL_ORIGIN),
  title: {
    default: `${SITE_NAME} - ${SITE_DESCRIPTION}`,
    template: `%s | ${SITE_NAME}`,
  },
  description:
    "Crie chatbots inteligentes para WhatsApp, Instagram e site sem código. Plataforma completa para automação de atendimento e marketing.",
  keywords: pageKeywords["home"].join(", "),
  authors: [{ name: "Sendbot Team", url: "https://sendbot.co" }],
  creator: "Sendbot",
  publisher: "Sendbot",
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  verification: {
    google: env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
    yandex: env.NEXT_PUBLIC_YANDEX_VERIFICATION,
  },
  openGraph: {
    type: "website",
    siteName: SITE_NAME,
    locale: "pt_BR",
    url: CANONICAL_ORIGIN,
    images: [
      {
        url: "/og-image.png",
        width: 1200,
        height: 630,
        alt: "Sendbot - Plataforma de chatbots para WhatsApp e Instagram",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    creator: "@SendbotHQ",
    site: "@SendbotHQ",
  },
  alternates: {
    canonical: CANONICAL_ORIGIN,
    languages: getLangAlternates("/"),
  },
  category: "technology",
  classification: "Business Software",
  appleWebApp: {
    capable: true,
    statusBarStyle: "black-translucent",
    title: SITE_NAME,
  },
  manifest: "/manifest.json",
  icons: {
    icon: [
      { url: "/favicon.svg", type: "image/svg+xml" },
      { url: "/icon-512.png", type: "image/png" },
    ],
    apple: [{ url: "/apple-touch-icon.png" }],
  },
  other: {
    "mobile-web-app-capable": "yes",
    HandheldFriendly: "True",
  },
};

export const baseViewport: Viewport = {
  themeColor: "#000000",
  width: "device-width",
  initialScale: 1,
  viewportFit: "cover",
};

// Legacy function for backwards compatibility
export function generateMetadata({
  title,
  description,
  image = "/og-image.png",
  path,
  keywords,
  locale = "pt",
  type = "website",
}: {
  title: string;
  description: string;
  image?: string;
  path: string;
  keywords?: string[];
  locale?: string;
  type?: "website" | "article";
}): Metadata {
  // Use page-specific keywords or the provided ones
  const pageType = path === "/" ? "home" : path.replace("/", "");
  const metaKeywords =
    keywords || pageKeywords[pageType] || pageKeywords["home"];

  // Get canonical URL
  const url = getCanonicalUrl(path, locale);
  const ogLocale = getOpenGraphLocale(locale);

  // Get social media tags
  const socialMeta = getSocialMetadata(title, description, image);

  return {
    title: {
      default: title,
      template: "%s | Sendbot",
    },
    description,
    keywords: metaKeywords.join(", "),
    openGraph: {
      ...socialMeta.openGraph,
      url,
      locale: ogLocale,
      type,
    },
    twitter: socialMeta.twitter,
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    verification: {
      google: env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
      yandex: env.NEXT_PUBLIC_YANDEX_VERIFICATION,
    },
    alternates: {
      canonical: url,
      languages: getLangAlternates(path),
    },
  };
}
